/*
 * label-maker/index.js — the tool.
 *
 * Wires the pure cores (sheets / data / layout) to the two backends
 * (render → SVG on screen, pdf → paper) and to the page's controls.
 *
 * THE ORDER OF OPERATIONS IS THE PRODUCT
 * ──────────────────────────────────────
 *   sheet geometry  →  design  →  records  →  laid-out cells  →  preview & PDF
 *
 * Everything downstream of "laid-out cells" is shared: what you see is the item
 * list that gets drawn on paper. Nothing re-decides a line break or a type size
 * on its way to the printer, because that is where preview/print divergence
 * comes from and it is unfixable once it exists.
 *
 * State lives in one object and is persisted through window.__toolSettings, so
 * a user who prints one class list on Monday finds the same sheet format and
 * the same printer correction on Tuesday. The correction in particular is
 * per-browser and long-lived on purpose: it describes a physical machine that
 * does not change between visits.
 */
(function () {
    'use strict';

    var CFG = window.__LABEL_MAKER__ || {};
    var T = CFG.t || {};
    var SLUG = 'label-maker';

    var S = window.LabelSheets, D = window.LabelData, LO = window.LabelLayout,
        R = window.LabelRender, P = window.LabelPresets, PDF = window.LabelPdf;

    /* Face id → CSS family. The @font-face rules live in the blade and point at
     * the same TTF files PdfTextFont embeds, so screen and paper use one file. */
    var FONTS = [
        ['noto', 'LM Noto Sans'], ['montserrat', 'LM Montserrat'], ['oswald', 'LM Oswald'],
        ['comfortaa', 'LM Comfortaa'], ['russoone', 'LM Russo One'], ['yeseva', 'LM Yeseva One'],
        ['playfair', 'LM Playfair'], ['lobster', 'LM Lobster'], ['caveat', 'LM Caveat'],
        ['marck', 'LM Marck Script'], ['pacifico', 'LM Pacifico'],
    ];
    var FAMILY = {};
    FONTS.forEach(function (f) { FAMILY[f[0]] = '"' + f[1] + '", sans-serif'; });
    function familyFor(id) { return FAMILY[id] || FAMILY.noto; }

    var SYMBOLOGIES = ['qrcode', 'code128', 'ean13', 'ean8', 'code39', 'datamatrix', 'itf14'];

    /* ── state ───────────────────────────────────────────────────────────── */
    var st = {
        fmtId: 'div-24',
        custom: null,
        correction: { scale: 1, dx: 0, dy: 0 },
        presetId: 'notebook',
        elements: [],
        selected: 0,
        dataMode: 'same',
        listText: '',
        tableText: '',
        table: null,
        seq: { prefix: '', start: 1, step: 1, count: 24, pad: 3, suffix: '' },
        skip: 0,
        copies: 1,
        order: 'row',
        outline: false,
        cutMarks: false,
        ruler: true,
        page: 0,
    };

    var els = {};                 // cached DOM
    var measure = null;           // text measurer, set once fonts are ready
    var barcodeCache = {};        // key → dataURL for preview
    var barcodeError = {};        // key → why bwip refused this value
    var barcodePending = {};
    var rafHandle = 0;

    /* ── tiny DOM helpers ────────────────────────────────────────────────── */
    function $(id) { return document.getElementById(id); }
    function on(node, ev, fn) { if (node) node.addEventListener(ev, fn); }
    function txt(node, s) { if (node) node.textContent = s == null ? '' : String(s); }
    function show(node, yes) { if (node) node.hidden = !yes; }
    function mk(tag, cls, text) {
        var n = document.createElement(tag);
        if (cls) n.className = cls;
        if (text != null) n.textContent = text;
        return n;
    }
    function fmtMm(v) { return S.fmt(v, 1); }
    function tr(key, fallback) { return T[key] != null ? T[key] : (fallback == null ? key : fallback); }
    function fill(s, vars) {
        return String(s).replace(/:(\w+)/g, function (m, k) {
            return Object.prototype.hasOwnProperty.call(vars, k) ? String(vars[k]) : m;
        });
    }

    /* ── format ──────────────────────────────────────────────────────────── */
    function currentFormat() {
        if (st.fmtId === 'custom') return st.custom || S.custom({ cols: 3, rows: 8 });
        return S.byId(st.fmtId) || S.byId('div-24');
    }

    function formatLabel(f) {
        var n = f.cols * f.rows;
        var size = fmtMm(f.w) + '×' + fmtMm(f.h);
        if (f.shape === 'circle') size = 'Ø' + fmtMm(f.w);
        if (f.family === 'vendor' || f.family === 'vendor-us') {
            return 'Avery ' + f.part + ' — ' + n + ' × ' + size + ' ' + tr('mm', 'мм');
        }
        if (f.family === 'division') return fill(tr('fmt_division', ':n на листе — :size мм'), { n: n, size: size });
        if (f.family === 'derived' || f.family === 'job') return fill(tr('fmt_' + f.id, ':n × :size мм'), { n: n, size: size });
        return fill(tr('fmt_custom_label', 'Свой лист — :n × :size мм'), { n: n, size: size });
    }

    function buildFormatSelect() {
        var sel = els.fmt;
        sel.textContent = '';
        var groups = [
            ['division', tr('grp_division', 'Делёный лист (без полей)')],
            ['vendor', tr('grp_vendor', 'Avery и совместимые')],
            ['vendor-us', tr('grp_vendor_us', 'US Letter (Avery)')],
            ['job', tr('grp_job', 'Визитки и маркетплейсы')],
            ['derived', tr('grp_derived', 'Круглые и корешки')],
        ];
        groups.forEach(function (g) {
            var og = document.createElement('optgroup');
            og.label = g[1];
            S.all().filter(function (f) { return f.family === g[0]; }).forEach(function (f) {
                var o = document.createElement('option');
                o.value = f.id;
                o.textContent = formatLabel(f);
                og.appendChild(o);
            });
            sel.appendChild(og);
        });
        var oc = document.createElement('option');
        oc.value = 'custom';
        oc.textContent = tr('fmt_custom', 'Свой лист…');
        sel.appendChild(oc);
        sel.value = st.fmtId;
    }

    function renderGeometry() {
        var f = currentFormat();
        var v = S.verifyGrid(f);
        var host = els.geom;
        host.textContent = '';

        var rows = [
            [tr('g_count', 'Этикеток на листе'), String(v.perSheet)],
            [tr('g_size', 'Размер этикетки'),
                (f.shape === 'circle' ? 'Ø' + fmtMm(f.w) : fmtMm(f.w) + ' × ' + fmtMm(f.h)) + ' ' + tr('mm', 'мм')],
            [tr('g_margins', 'Поля (Л/В/П/Н)'),
                [v.margins.left, v.margins.top, v.margins.right, v.margins.bottom].map(fmtMm).join(' / ')],
            [tr('g_gaps', 'Зазоры (X/Y)'), fmtMm(v.gaps.x) + ' / ' + fmtMm(v.gaps.y)],
        ];
        rows.forEach(function (r) {
            var line = mk('div', 'lm-kv');
            line.appendChild(mk('span', 'lm-k', r[0]));
            line.appendChild(mk('span', 'lm-v', r[1]));
            host.appendChild(line);
        });

        // Where the numbers come from is part of the answer, not a footnote:
        // a `derived` layout is a good guess and the user should check it.
        var srcNote = mk('div', 'lm-src lm-src-' + f.src);
        srcNote.textContent = tr('src_' + f.src, '');
        if (f.confirmedBy) srcNote.textContent += ' (' + f.confirmedBy + ')';
        if (srcNote.textContent) host.appendChild(srcNote);

        var warn = els.geomWarn;
        warn.textContent = '';
        v.findings.forEach(function (fd) {
            var b = mk('div', 'lm-note lm-note-' + fd.level);
            if (fd.code === 'dead_zone') {
                b.textContent = fill(tr(fd.level === 'info' ? 'note_dead_zone_bleed' : 'warn_dead_zone', ''),
                    { mm: fd.mm });
            } else if (fd.code === 'overlap_x' || fd.code === 'overlap_y') {
                b.textContent = fill(tr('warn_overlap', 'Этикетки налезают друг на друга на :mm мм.'), { mm: fd.mm });
            } else {
                b.textContent = fill(tr('warn_outside', 'Сетка выходит за лист на :mm мм.'), { mm: fd.mm || 0 });
            }
            warn.appendChild(b);
        });
        show(warn, v.findings.length > 0);
    }

    /* ── custom sheet dialog ─────────────────────────────────────────────── */
    function openCustom() {
        var f = currentFormat();
        ['cols', 'rows', 'w', 'h', 'x0', 'y0', 'dx', 'dy'].forEach(function (k) {
            if (els['c_' + k]) els['c_' + k].value = k === 'cols' || k === 'rows' ? f[k] : fmtMm(f[k]);
        });
        if (els.c_shape) els.c_shape.value = f.shape === 'circle' ? 'circle' : (f.radius > 0.6 ? 'round' : 'rect');
        if (els.c_radius) els.c_radius.value = fmtMm(f.radius || 0);
        els.customDlg.hidden = false;
        syncCustomPreview();
    }

    function readCustom(auto) {
        var spec = {
            cols: els.c_cols.value, rows: els.c_rows.value,
            w: els.c_w.value, h: els.c_h.value,
            shape: els.c_shape.value, radius: els.c_radius.value,
        };
        if (!auto) {
            spec.x0 = els.c_x0.value; spec.y0 = els.c_y0.value;
            spec.dx = els.c_dx.value; spec.dy = els.c_dy.value;
        }
        return S.custom(spec);
    }

    function syncCustomPreview() {
        var auto = els.c_auto.checked;
        ['x0', 'y0', 'dx', 'dy'].forEach(function (k) { els['c_' + k].disabled = auto; });
        var f = readCustom(auto);
        if (auto) {
            els.c_x0.value = fmtMm(f.x0); els.c_y0.value = fmtMm(f.y0);
            els.c_dx.value = fmtMm(f.dx); els.c_dy.value = fmtMm(f.dy);
        }
        var v = S.verifyGrid(f);
        txt(els.c_summary, fill(tr('c_summary', 'На листе: :n. Поля Л/В/П/Н: :m мм'), {
            n: v.perSheet,
            m: [v.margins.left, v.margins.top, v.margins.right, v.margins.bottom].map(fmtMm).join(' / '),
        }));
        els.c_summary.classList.toggle('is-bad', !v.ok);
        R.renderSheet(els.c_preview, f, [], { showNumbers: false });
    }

    /* ── design ──────────────────────────────────────────────────────────── */
    function applyPreset(id) {
        var p = P.byId(id);
        if (!p) return;
        if (st.elements.length) pushUndo();
        st.presetId = id;
        st.elements = p.elements.map(function (e) { return JSON.parse(JSON.stringify(e)); });
        st.selected = 0;
        renderPresetGallery();
        renderElementList();
        renderEditor();
        schedule();
    }

    function renderPresetGallery() {
        var host = els.presets;
        host.textContent = '';
        var f = currentFormat();
        P.forFormat(f).forEach(function (p) {
            var b = mk('button', 'lm-preset' + (p.id === st.presetId ? ' is-on' : ''));
            b.type = 'button';
            /* Опознавательный атрибут, а не позиция в списке: набор пресетов
               зависит от формата, и тест «третья кнопка» ломается от любого
               нового оформления. */
            b.setAttribute('data-preset', p.id);
            b.setAttribute('aria-pressed', p.id === st.presetId ? 'true' : 'false');
            var thumb = mk('div', 'lm-preset-thumb');
            b.appendChild(thumb);
            b.appendChild(mk('span', 'lm-preset-name', tr('preset_' + p.id, p.id)));
            on(b, 'click', function () {
                applyPreset(p.id);
                if (window.__aeFunnel) window.__aeFunnel('preset', { id: p.id });
            });
            host.appendChild(b);

            // Thumbnails are the same renderer at a small size — a preset that
            // would not render is then visibly broken here rather than after
            // the user picks it.
            var lay = LO.layoutLabel({ w: f.w, h: f.h, safe: 1.5 }, p.elements, {
                measure: measure || LO.defaultMeasure,
                resolve: function (el) { return sampleFor(el); },
            });
            R.renderLabel(thumb, f, lay, { fontFamilyFor: familyFor, barcodeUrl: barcodeUrl });
        });
    }

    /* Placeholder content so an empty design still looks like something.
     *
     * ⚠️ Подставляются ВСЕ токены, а не первый. Прежняя версия брала первое
     * совпадение и возвращала ОДИН образец вместо строки, поэтому
     * «{{цвет}} · {{размер}}» показывалось как «чёрный»: разделитель и второе
     * поле молча исчезали, и превью врало про макет, который на данных
     * отрисуется правильно. */
    function sampleFor(el) {
        var t = String(el.text || '');
        if (!/\{\{/.test(t)) return t;
        var samples = CFG.samples || {};
        if (el.type === 'barcode') {
            // Штрихкод — это ЗНАЧЕНИЕ целиком, склеивать в него разделители
            // нельзя: bwip либо не закодирует, либо закодирует мусор.
            // A valid EAN-13: the check digit of 460000000000 is 8. An invalid
            // one renders as nothing at all (bwip throws), so a wrong sample
            // makes the product preset look broken rather than wrong.
            var first = (t.match(/\{\{\s*([^}]+?)\s*\}\}/) || [])[1] || '';
            if (samples[first]) return samples[first];
            return el.symbology === 'ean13' ? '4600000000008' : (samples.value || 'ABC-001');
        }
        return t.replace(/\{\{\s*([^}]+?)\s*\}\}/g, function (m, name) {
            return samples[name] != null ? samples[name] : (samples.value || 'Образец');
        });
    }

    function renderElementList() {
        var host = els.elList;
        host.textContent = '';
        st.elements.forEach(function (e, i) {
            var row = mk('div', 'lm-el' + (i === st.selected ? ' is-on' : ''));
            var pick = mk('button', 'lm-el-pick');
            pick.type = 'button';
            pick.textContent = elementTitle(e);
            on(pick, 'click', function () { st.selected = i; renderElementList(); renderEditor(); });
            row.appendChild(pick);

            var eye = mk('button', 'lm-el-btn', e.hidden ? '◌' : '●');
            eye.type = 'button';
            eye.dataset.act = 'hide';
            eye.title = tr('el_toggle', 'Показать/скрыть');
            eye.setAttribute('aria-label', tr('el_toggle', 'Показать/скрыть'));
            on(eye, 'click', function () { e.hidden = !e.hidden; renderElementList(); schedule(); });
            row.appendChild(eye);

            // Duplicate. Without it, "the same line again, one size smaller"
            // means rebuilding every property by hand — the first thing anyone
            // wants after styling one block.
            var dup = mk('button', 'lm-el-btn', '⧉');
            dup.type = 'button';
            dup.dataset.act = 'duplicate';
            dup.title = tr('el_duplicate', '');
            dup.setAttribute('aria-label', tr('el_duplicate', ''));
            on(dup, 'click', function () {
                pushUndo();
                var copy = JSON.parse(JSON.stringify(e));
                // Offset so the copy is visible instead of hiding under the
                // original, and keep it inside the safe area.
                copy.x = Math.min(0.92, num(copy.x, 0) + 0.04);
                copy.y = Math.min(0.92, num(copy.y, 0) + 0.06);
                st.elements.splice(i + 1, 0, copy);
                st.selected = i + 1;
                renderElementList(); renderEditor(); schedule();
            });
            row.appendChild(dup);

            var del = mk('button', 'lm-el-btn', '×');
            del.type = 'button';
            del.dataset.act = 'delete';
            del.title = tr('el_delete', 'Удалить');
            del.setAttribute('aria-label', tr('el_delete', 'Удалить'));
            on(del, 'click', function () {
                pushUndo();
                st.elements.splice(i, 1);
                st.selected = Math.max(0, Math.min(st.selected, st.elements.length - 1));
                renderElementList(); renderEditor(); schedule();
            });
            row.appendChild(del);
            host.appendChild(row);
        });
    }

    function elementTitle(e) {
        var kind = tr('kind_' + e.type, e.type);
        var extra = e.type === 'text' ? String(e.text || '').slice(0, 18)
            : (e.type === 'barcode' ? e.symbology : '');
        return extra ? kind + ' · ' + extra : kind;
    }

    function renderEditor() {
        var host = els.editor;
        host.textContent = '';
        var e = st.elements[st.selected];
        if (!e) { show(els.editorWrap, false); return; }
        show(els.editorWrap, true);

        function field(labelText, node) {
            var f = mk('label', 'lm-field');
            f.appendChild(mk('span', 'lm-flabel', labelText));
            f.appendChild(node);
            host.appendChild(f);
            return node;
        }
        function input(type, val, onInput, attrs) {
            var i = document.createElement('input');
            i.type = type;
            i.className = 'lm-input';
            i.value = val == null ? '' : val;
            for (var k in attrs || {}) i.setAttribute(k, attrs[k]);
            on(i, 'input', function () { onInput(i.value); schedule(); });
            return i;
        }
        function select(options, val, onChange) {
            var s = document.createElement('select');
            s.className = 'lm-input';
            options.forEach(function (o) {
                var op = document.createElement('option');
                op.value = o[0]; op.textContent = o[1];
                s.appendChild(op);
            });
            s.value = val;
            on(s, 'change', function () { onChange(s.value); schedule(); });
            return s;
        }

        if (e.type === 'text') {
            var ta = document.createElement('textarea');
            ta.className = 'lm-input lm-textarea';
            ta.rows = 2;
            ta.value = e.text || '';
            ta.setAttribute('data-voice-punctuation', 'off');
            on(ta, 'input', function () { e.text = ta.value; renderElementList(); schedule(); });
            field(tr('f_text', 'Текст'), ta);
            renderFieldChips(host, function (token) {
                e.text = (e.text || '') + token;
                ta.value = e.text;
                renderElementList(); schedule();
            });

            var fontSel = select(FONTS.map(function (f) { return [f[0], f[1].replace('LM ', '')]; }),
                e.font || 'noto', function (v) { e.font = v; });
            // Marked so the metrics guard can find the offered faces without
            // guessing which <select> is which — a face added here without
            // being measured is exactly what that test exists to catch.
            fontSel.setAttribute('data-lm-fonts', '1');
            field(tr('f_font', 'Шрифт'), fontSel);
            field(tr('f_size', 'Размер, % высоты'),
                input('number', Math.round((e.sizeRel != null ? e.sizeRel : 0.3) * 100),
                    function (v) { e.sizeRel = Math.max(2, Math.min(100, parseFloat(v) || 30)) / 100; delete e.size; },
                    { min: 2, max: 100, step: 1 }));
            field(tr('f_align', 'Выключка'), select([
                ['left', tr('al_left', 'по левому')], ['center', tr('al_center', 'по центру')],
                ['right', tr('al_right', 'по правому')],
            ], e.align || 'center', function (v) { e.align = v; }));
            field(tr('f_valign', 'По вертикали'), select([
                ['top', tr('va_top', 'вверх')], ['middle', tr('va_middle', 'по центру')],
                ['bottom', tr('va_bottom', 'вниз')],
            ], e.valign || 'middle', function (v) { e.valign = v; }));
            field(tr('f_color', 'Цвет'), input('color', e.color || '#000000', function (v) { e.color = v; }));

            var bold = document.createElement('input');
            bold.type = 'checkbox'; bold.checked = !!e.bold; bold.className = 'lm-check';
            on(bold, 'change', function () { e.bold = bold.checked; schedule(); });
            var bl = mk('label', 'lm-inline');
            bl.appendChild(bold); bl.appendChild(mk('span', null, tr('f_bold', 'Полужирный')));
            host.appendChild(bl);
        } else if (e.type === 'barcode') {
            field(tr('f_symbology', 'Тип кода'), select(SYMBOLOGIES.map(function (s) { return [s, tr('sym_' + s, s)]; }),
                e.symbology || 'qrcode', function (v) { e.symbology = v; barcodeCache = {}; }));
            var bi = input('text', e.text || '', function (v) { e.text = v; });
            field(tr('f_value', 'Значение'), bi);
            renderFieldChips(host, function (token) { e.text = (e.text || '') + token; bi.value = e.text; schedule(); });
        } else if (e.type === 'image') {
            var fi = document.createElement('input');
            fi.type = 'file'; fi.accept = 'image/png,image/jpeg'; fi.className = 'lm-input';
            on(fi, 'change', function () {
                var file = fi.files && fi.files[0];
                if (!file) return;
                var rd = new FileReader();
                rd.onload = function () {
                    // A file the browser cannot decode used to be accepted in
                    // silence: nothing appeared on the label and nothing said
                    // why. `accept=` filters the picker, not a drop or a renamed
                    // file, so the only honest gate is actually decoding it.
                    var probe = new Image();
                    probe.onload = function () { e.src = rd.result; schedule(); };
                    probe.onerror = function () {
                        if (window.__toastBlocked) {
                            window.__toastBlocked(tr('err_not_image', ''), 'not_an_image');
                        } else if (window.__toast) {
                            window.__toast(tr('err_not_image', ''), 'error');
                        }
                        fi.value = '';
                    };
                    probe.src = rd.result;
                };
                rd.onerror = function () {
                    if (window.__toast) window.__toast(tr('err_not_image', ''), 'error');
                };
                rd.readAsDataURL(file);
            });
            field(tr('f_image', 'Картинка'), fi);
            field(tr('f_fit', 'Вписывание'), select([
                ['contain', tr('fit_contain', 'целиком')], ['cover', tr('fit_cover', 'заполнить')],
            ], e.fit || 'contain', function (v) { e.fit = v; }));
        } else if (e.type === 'shape') {
            field(tr('f_shape', 'Фигура'), select([
                ['rect', tr('sh_rect', 'прямоугольник')], ['circle', tr('sh_circle', 'круг')],
                ['line', tr('sh_line', 'линия')],
            ], e.shape || 'rect', function (v) { e.shape = v; }));
            field(tr('f_fill', 'Заливка'), input('color', e.fill || '#eeeeee', function (v) { e.fill = v; }));
            field(tr('f_stroke', 'Обводка'), input('color', e.stroke || '#333333', function (v) { e.stroke = v; }));
        }

        /* Alignment helpers. Typing "X = 12 %" to centre something is the kind
         * of arithmetic a constructor is supposed to remove; these do the three
         * moves people actually make, and they write the SAME fractions the
         * fields and the drag do — no second source of truth. */
        var alignRow = mk('div', 'lm-chips');
        alignRow.appendChild(mk('span', 'lm-chips-label', tr('f_place', '')));
        [
            ['center_h', tr('al_center_h', ''), function () {
                e.x = Math.max(0, (1 - num(e.w, 1)) / 2);
            }],
            ['center_v', tr('al_center_v', ''), function () {
                e.y = Math.max(0, (1 - num(e.h, 1)) / 2);
            }],
            ['full_w', tr('al_full_w', ''), function () { e.x = 0; e.w = 1; }],
        ].forEach(function (a) {
            var b = mk('button', 'lm-chip', a[1]);
            b.type = 'button';
            on(b, 'click', function () {
                pushUndo();
                a[2]();
                renderEditor();
                schedule();
            });
            alignRow.appendChild(b);
        });
        host.appendChild(alignRow);

        // Position and size are shared by every element type.
        var grid = mk('div', 'lm-xy');
        [['x', tr('f_x', 'X, %')], ['y', tr('f_y', 'Y, %')],
        ['w', tr('f_w', 'Ш, %')], ['h', tr('f_h', 'В, %')]].forEach(function (p) {
            var l = mk('label', 'lm-field');
            l.appendChild(mk('span', 'lm-flabel', p[1]));
            var i = document.createElement('input');
            i.type = 'number'; i.className = 'lm-input'; i.min = 0; i.max = 100; i.step = 1;
            i.value = Math.round((e[p[0]] != null ? e[p[0]] : (p[0] === 'w' || p[0] === 'h' ? 1 : 0)) * 100);
            on(i, 'input', function () {
                e[p[0]] = Math.max(0, Math.min(100, parseFloat(i.value) || 0)) / 100;
                schedule();
            });
            l.appendChild(i);
            grid.appendChild(l);
        });
        host.appendChild(grid);

        /* Две колонки для коротких полей, вся ширина — для длинных. Решается
           здесь, а не в CSS через :has(), потому что «широкое» — это свойство
           содержимого (текст, чипсы, сетка координат), и знает его тот, кто
           поле создал. */
        for (var ci = 0; ci < host.children.length; ci++) {
            var ch = host.children[ci];
            var wide = !ch.classList.contains('lm-field')
                || !!ch.querySelector('textarea')
                || !!ch.querySelector('input[type=color]');
            ch.classList.toggle('lm-wide', wide);
        }
    }

    /* Clickable merge fields — typing `{{класс}}` by hand invites the typo that
     * prints 40 blank labels, so the available names are offered as chips. */
    function renderFieldChips(host, add) {
        var names = availableFields();
        if (!names.length) return;
        var wrap = mk('div', 'lm-chips');
        wrap.appendChild(mk('span', 'lm-chips-label', tr('f_insert', 'Вставить поле:')));
        names.forEach(function (n) {
            var c = mk('button', 'lm-chip', n);
            c.type = 'button';
            on(c, 'click', function () { add('{{' + n + '}}'); });
            wrap.appendChild(c);
        });
        host.appendChild(wrap);
    }

    function availableFields() {
        if (st.dataMode === 'table' && st.table && st.table.fields) return st.table.fields.concat(['#']);
        if (st.dataMode === 'list') return ['value', '#'];
        if (st.dataMode === 'seq') return ['value', '#'];
        return [];
    }

    /* ── data ────────────────────────────────────────────────────────────── */
    function records() {
        if (st.dataMode === 'list') return D.parseList(st.listText);
        if (st.dataMode === 'table') return (st.table && st.table.rows) || [];
        if (st.dataMode === 'seq') return D.sequence(st.seq);
        return null;                       // 'same' — fill the sheet with one design
    }

    function cellsForSheet() {
        var f = currentFormat();
        var per = S.perSheet(f);
        var recs = records();
        var list;
        if (recs === null) {
            list = [];
            for (var i = 0; i < st.skip; i++) list.push(null);
            while (list.length < per) list.push({});
        } else {
            list = D.expand(recs, { copies: st.copies, skip: st.skip });
            if (!list.length) { list = []; for (var j = 0; j < st.skip; j++) list.push(null); }
        }
        return list;
    }

    function layoutCells(list) {
        var f = currentFormat();
        var label = { w: f.w, h: f.h, safe: 1.5 };
        var m = measure || LO.defaultMeasure;
        return list.map(function (rec, i) {
            if (rec === null) return null;
            return LO.layoutLabel(label, st.elements, {
                measure: m,
                resolve: function (el) {
                    var t = el.text || '';
                    if (st.dataMode === 'same') return /\{\{/.test(t) ? sampleFor(el) : t;
                    return D.merge(t, rec, i - st.skip);
                },
            });
        });
    }

    function renderDataPanel() {
        show(els.d_list, st.dataMode === 'list');
        show(els.d_table, st.dataMode === 'table');
        show(els.d_seq, st.dataMode === 'seq');
        Array.prototype.forEach.call(els.dataTabs.querySelectorAll('button'), function (b) {
            var on_ = b.dataset.mode === st.dataMode;
            b.classList.toggle('is-on', on_);
            b.setAttribute('aria-selected', on_ ? 'true' : 'false');
        });
        renderEditor();
    }

    function ingestTable(text) {
        st.tableText = text;
        st.table = D.parseTable(text);
        var host = els.tablePreview;
        host.textContent = '';
        if (!st.table.rows.length) { txt(els.tableNote, tr('tbl_empty', 'Пока пусто')); return; }
        var tb = mk('table', 'lm-table');
        var head = mk('tr');
        (st.table.fields || []).forEach(function (f) { head.appendChild(mk('th', null, f)); });
        tb.appendChild(head);
        st.table.rows.slice(0, 4).forEach(function (r) {
            var tr_ = mk('tr');
            (st.table.fields || []).forEach(function (f) { tr_.appendChild(mk('td', null, r[f] || '')); });
            tb.appendChild(tr_);
        });
        host.appendChild(tb);
        txt(els.tableNote, fill(tr('tbl_note', 'Записей: :n · разделитель «:d»'), {
            n: st.table.rows.length,
            d: st.table.delimiter === '\t' ? 'Tab' : st.table.delimiter,
        }));
    }

    /* ── audit + summary ─────────────────────────────────────────────────── */
    function renderSummary(list, laid) {
        var f = currentFormat();
        var per = S.perSheet(f);
        var recs = records();
        var plan = D.plan(list.length, per);
        var host = els.summary;
        host.textContent = '';

        var line = mk('div', 'lm-sum-main');
        if (recs === null) {
            line.textContent = fill(tr('sum_same', ''), { n: per - st.skip });
        } else if (!recs.length) {
            // "Наклеек: 0 · листов: 0" is arithmetic, not an answer: to a user
            // who has just opened the Table tab it reads as breakage. Name the
            // missing step instead.
            line.textContent = tr(st.dataMode === 'table' ? 'sum_need_table'
                : st.dataMode === 'list' ? 'sum_need_list' : 'sum_need_data', '');
        } else {
            line.textContent = fill(tr('sum_run', ''), plan);
        }
        host.appendChild(line);

        // The pre-flight. This is the difference between finding out now and
        // finding out on paper.
        var over = [], tiny = [], tinySize = null;
        laid.forEach(function (c, i) {
            if (!c) return;
            if (c.issues.some(function (x) { return x.code === 'text_overflow'; })) over.push(i);
            var small = c.issues.filter(function (x) { return x.code === 'text_tiny'; })[0];
            if (small) { tiny.push(i); if (tinySize == null || small.size < tinySize) tinySize = small.size; }
        });
        var warn = els.runWarn;
        warn.textContent = '';
        if (over.length) {
            var b = mk('div', 'lm-note lm-note-warn');
            b.textContent = fill(tr('warn_overflow',
                'Не помещается записей: :n (например №:first). Текст будет обрезан — уменьшите кегль, добавьте строк или сократите значения.'),
                { n: over.length, first: over[0] + 1 });
            warn.appendChild(b);
            var jump = mk('button', 'lm-linkbtn', tr('warn_overflow_show', 'Показать первую'));
            jump.type = 'button';
            on(jump, 'click', function () {
                st.page = Math.floor(over[0] / per);
                schedule();
            });
            warn.appendChild(jump);
        }
        if (tiny.length) {
            var tn = mk('div', 'lm-note lm-note-warn');
            tn.textContent = fill(tr('warn_tiny', ''), { n: tiny.length, size: S.fmt(tinySize, 1) });
            warn.appendChild(tn);
        }
        // A barcode that will not encode: say so, and name the symbology.
        // Silence here is indistinguishable from "still rendering".
        var badCode = null;
        laid.forEach(function (c) {
            if (badCode || !c) return;
            (c.items || []).forEach(function (it) {
                if (badCode || it.kind !== 'barcode') return;
                if (barcodeError[barcodeKey(it)]) badCode = { sym: it.symbology, value: it.value };
            });
        });
        if (badCode) {
            var bn = mk('div', 'lm-note lm-note-warn');
            bn.textContent = fill(tr('warn_barcode', ''), {
                sym: tr('sym_' + badCode.sym, badCode.sym),
                value: String(badCode.value).slice(0, 24),
            });
            warn.appendChild(bn);
        }

        if (!st.elements.filter(function (e) { return !e.hidden; }).length) {
            var empty = mk('div', 'lm-note lm-note-warn');
            empty.textContent = tr('warn_empty_design', '');
            warn.appendChild(empty);
        }

        var audit = D.auditTokens(st.elements.map(function (e) { return e.text || ''; }), availableFields());
        if (audit.unknown.length && st.dataMode !== 'same') {
            var u = mk('div', 'lm-note lm-note-warn');
            u.textContent = fill(tr('warn_unknown_field', 'Неизвестные поля: :list. Они напечатаются пустыми.'),
                { list: audit.unknown.join(', ') });
            warn.appendChild(u);
        }
        show(warn, warn.childNodes.length > 0);
    }

    /* ── barcodes for the preview ────────────────────────────────────────── */
    function barcodeKey(it) { return R.barcodeKey(it); }
    function barcodeUrl(it) {
        var k = barcodeKey(it);
        if (barcodeCache[k]) return barcodeCache[k];
        if (!barcodePending[k] && String(it.value || '').length) {
            barcodePending[k] = true;
            ensureBwip().then(function (bwip) {
                var c = document.createElement('canvas');
                try {
                    // Same options builder the PDF uses — only the raster scale
                    // is allowed to differ between screen and paper.
                    bwip.toCanvas(c, R.barcodeOptions(it, 3));
                    barcodeCache[k] = c.toDataURL('image/png');
                } catch (e) {
                    // An invalid value WHILE TYPING is normal — a toast on every
                    // keystroke would be worse than the problem. But an empty
                    // dashed frame with no explanation reads as "still loading",
                    // so the reason is remembered and surfaced once, in the same
                    // pre-flight block as every other finding.
                    barcodeCache[k] = '';
                    barcodeError[k] = String((e && e.message) || e).slice(0, 140);
                }
                schedule();
            }).catch(function () { barcodeCache[k] = ''; });
        }
        return barcodeCache[k] || null;
    }
    /* One loader for the preview and the PDF: PDF.ensureBwip already walks the
     * cdn_chain list and caches, so the preview reuses it instead of racing a
     * second <script> for the same 1.1 MB. */
    function ensureBwip() {
        return PDF.ensureBwip();
    }

    /* ── the render loop ─────────────────────────────────────────────────── */
    function schedule() {
        if (rafHandle) return;
        rafHandle = requestAnimationFrame(function () {
            rafHandle = 0;
            draw();
        });
    }

    function draw() {
        var f = currentFormat();
        var per = S.perSheet(f);
        var list = cellsForSheet();
        var laid = layoutCells(list);
        var pages = Math.max(1, Math.ceil(list.length / per));
        if (st.page >= pages) st.page = pages - 1;

        R.renderSheet(els.sheet, f, laid, {
            order: st.order,
            correction: st.correction,
            page: st.page,
            showNumbers: els.showNumbers ? els.showNumbers.checked : true,
            showDeadZone: els.showDead ? els.showDead.checked : false,
            ruler: st.ruler,
            fontFamilyFor: familyFor,
            barcodeUrl: barcodeUrl,
            ariaLabel: tr('sheet_aria', 'Предпросмотр листа'),
        });

        var firstIdx = 0;
        while (firstIdx < laid.length && !laid[firstIdx]) firstIdx++;
        oneContent = laid[firstIdx] || null;
        oneFmt = f;
        R.renderLabel(els.one, f, oneContent, {
            fontFamilyFor: familyFor,
            barcodeUrl: barcodeUrl,
            showSafe: els.showSafe ? els.showSafe.checked : true,
            safe: 1.5,
            selected: visibleItemIndex(oneContent, st.selected),
        });

        txt(els.pageLabel, fill(tr('page_of', 'Лист :a из :b'), { a: st.page + 1, b: pages }));
        els.pagePrev.disabled = st.page <= 0;
        els.pageNext.disabled = st.page >= pages - 1;

        renderGeometry();
        renderSummary(list, laid);
        renderStepSummaries(list, D.plan(list.length, per));
        persist();
    }

    /* ── direct manipulation ─────────────────────────────────────────────────
     * A constructor whose only positioning control is four percentage fields is
     * a form, not a designer. Dragging on the big label preview edits the same
     * `x/y/w/h` fractions the number inputs do, so nothing gains a second
     * source of truth — the fields simply follow.
     */

    /* An element can be hidden or skipped, so the index in `st.elements` is not
     * the index in the drawn item list. Map one to the other. */
    function visibleItemIndex(content, elementIndex) {
        if (!content || !content.items) return null;
        for (var i = 0; i < content.items.length; i++) {
            if (content.items[i].el === st.elements[elementIndex]) return i;
        }
        return null;
    }
    function elementIndexOfItem(content, itemIndex) {
        if (!content || !content.items || !content.items[itemIndex]) return -1;
        return st.elements.indexOf(content.items[itemIndex].el);
    }

    var MOVE_THRESHOLD_MM = 0.6;   // below this a pointerdown is still a click

    /* Latest laid-out label + format, refreshed by draw(). The gesture reads
     * these rather than closing over them, because draw() replaces the whole
     * SVG on every frame. */
    var oneContent = null, oneFmt = null;

    /* Bound ONCE, to the HOST — not to the SVG.
     *
     * The obvious version attaches to the <svg> that renderLabel just built.
     * It does not work: the first pointermove updates the model, draw() runs,
     * the SVG is replaced, and the node holding the listeners (and the pointer
     * capture) is detached mid-gesture. The drag then dies after one frame and
     * looks like "nothing happens". The host div survives every redraw. */
    function attachLabelDrag(host) {
        if (!host || host.__lmDragBound) return;
        host.__lmDragBound = true;
        host.style.touchAction = 'none';

        host.addEventListener('pointerdown', function (ev) {
            if (ev.button != null && ev.button !== 0) return;
            var svg = host.querySelector('svg');
            var content = oneContent, fmt = oneFmt;
            if (!svg || !fmt || !content || !content.items || !content.items.length) return;
            var p = R.svgPoint(svg, ev.clientX, ev.clientY);
            var onGrip = ev.target && ev.target.getAttribute
                && ev.target.getAttribute('data-lm-grip') === '1';

            var itemIdx = onGrip ? visibleItemIndex(content, st.selected) : R.hitTest(content.items, p.x, p.y);
            if (itemIdx == null || itemIdx < 0) return;

            var elIdx = elementIndexOfItem(content, itemIdx);
            if (elIdx < 0) return;
            var target = st.elements[elIdx];

            if (elIdx !== st.selected) {
                st.selected = elIdx;
                renderElementList();
                renderEditor();
            }

            var start = { x: p.x, y: p.y };
            var box = content.items[itemIdx].box;
            var origin = {
                x: num(target.x, 0), y: num(target.y, 0),
                w: num(target.w, 1), h: num(target.h, 1),
            };
            // The fractions are of the SAFE AREA, not the whole label, so the
            // conversion from millimetres has to use the same inner box the
            // layout used — otherwise a drag drifts away from the pointer.
            var inset = 1.5;
            var innerW = Math.max(1, fmt.w - inset * 2);
            var innerH = Math.max(1, fmt.h - inset * 2);

            var moved = false, captured = false;

            function onMove(e2) {
                var live = host.querySelector('svg');
                if (!live) return;
                var q = R.svgPoint(live, e2.clientX, e2.clientY);
                var dx = q.x - start.x, dy = q.y - start.y;
                if (!moved && Math.abs(dx) < MOVE_THRESHOLD_MM && Math.abs(dy) < MOVE_THRESHOLD_MM) return;
                if (!moved) {
                    moved = true;
                    pushUndo();
                    // Capture only once the gesture is definitely a drag.
                    // Capturing on pointerdown swallows the click that selects
                    // an element — the exact failure recorded in
                    // reference_pointer_capture_on_down_kills_click. The host
                    // is the capture target because it outlives the redraw.
                    try { host.setPointerCapture(e2.pointerId); captured = true; } catch (_) { }
                }
                // Both gestures stay inside the safe area: a block dragged or
                // stretched past it would print over the die cut, and the tool
                // would then warn about a position it had just helped make.
                // Both gestures stay inside the safe area. The two clamps are
                // NOT the same function: a position may legitimately be 0, a
                // size may not, and giving a position the size's minimum is
                // what pushed a full-width block 2 mm past the die cut.
                if (onGrip) {
                    target.w = clampSize(origin.w + dx / innerW, num(target.x, 0));
                    target.h = clampSize(origin.h + dy / innerH, num(target.y, 0));
                } else {
                    target.x = clampPos(origin.x + dx / innerW, num(target.w, 1));
                    target.y = clampPos(origin.y + dy / innerH, num(target.h, 1));
                }
                schedule();
            }
            function onUp(e2) {
                host.removeEventListener('pointermove', onMove);
                host.removeEventListener('pointerup', onUp);
                host.removeEventListener('pointercancel', onUp);
                if (captured) { try { host.releasePointerCapture(e2.pointerId); } catch (_) { } }
                if (moved) { renderEditor(); schedule(); }
            }
            host.addEventListener('pointermove', onMove);
            host.addEventListener('pointerup', onUp);
            host.addEventListener('pointercancel', onUp);
            // Not preventDefault here: a plain tap must still reach the browser
            // as a click so focus and accessibility behave normally.
        });
    }

    var MIN_SPAN = 0.05;   // a block smaller than this cannot be grabbed again

    /* Position: 0 is fine, and the block must end at or before the safe edge.
     * A full-width block therefore simply cannot move sideways — correct, and
     * better than letting it slide past the cut. */
    function clampPos(v, size) {
        if (!isFinite(v)) return 0;
        var room = Math.max(0, 1 - (isFinite(size) ? size : 1));
        return Math.max(0, Math.min(room, v));
    }
    /* Size: never zero, never past the safe edge from where the block starts. */
    function clampSize(v, pos) {
        if (!isFinite(v)) return MIN_SPAN;
        var room = Math.max(MIN_SPAN, 1 - (isFinite(pos) ? pos : 0));
        return Math.max(MIN_SPAN, Math.min(room, v));
    }
    function num(v, d) {
        v = typeof v === 'string' ? parseFloat(v.replace(',', '.')) : v;
        return (typeof v === 'number' && isFinite(v)) ? v : d;
    }

    /* ── undo ────────────────────────────────────────────────────────────────
     * Only the design is undoable, because that is the part a user destroys by
     * accident — a deleted block or a dragged-off logo. Sheet format and data
     * are deliberate choices with their own visible controls. */
    var undoStack = [];
    function pushUndo() {
        try {
            undoStack.push(JSON.stringify(st.elements));
            if (undoStack.length > 40) undoStack.shift();
        } catch (e) { /* an element with a huge data: URI — skip this step */ }
    }
    function undo() {
        var prev = undoStack.pop();
        if (!prev) return false;
        try { st.elements = JSON.parse(prev); } catch (e) { return false; }
        st.selected = Math.min(st.selected, Math.max(0, st.elements.length - 1));
        renderElementList(); renderEditor(); schedule();
        return true;
    }

    /* ── printer correction ──────────────────────────────────────────────── */
    var CORR_KEY = 'label-maker-correction';

    function loadCorrection() {
        try {
            var raw = window.__toolSettings && window.__toolSettings.load(SLUG, CORR_KEY, null);
            if (raw && typeof raw === 'object') st.correction = S.correction(raw);
        } catch (e) { /* first visit */ }
    }
    function saveCorrection() {
        try {
            if (window.__toolSettings) window.__toolSettings.save(SLUG, CORR_KEY, st.correction);
        } catch (e) { /* private mode */ }
    }

    function renderCorrection() {
        var c = st.correction;
        els.k_scale.value = S.fmt((c.scale - 1) * 100, 2);
        els.k_dx.value = S.fmt(c.dx, 2);
        els.k_dy.value = S.fmt(c.dy, 2);
        var active = Math.abs(c.scale - 1) > 1e-6 || c.dx || c.dy;
        els.calibBadge.hidden = !active;
        txt(els.calibBadge, active
            ? fill(tr('calib_on', 'Поправка: :s % · :dx / :dy мм'),
                { s: S.fmt((c.scale - 1) * 100, 2), dx: S.fmt(c.dx, 2), dy: S.fmt(c.dy, 2) })
            : '');
    }

    function applyMeasuredScale() {
        var nominal = parseFloat(String(els.k_nominal.value).replace(',', '.'));
        var measured = parseFloat(String(els.k_measured.value).replace(',', '.'));
        var r = S.solveScale(nominal, measured);
        if (!r.ok) {
            var msg = r.code === 'out_of_range'
                ? tr('calib_out_of_range', 'Такое расхождение принтер дать не может — проверьте, что измеряли ту самую линию и что лист печатался как A4.')
                : tr('calib_need_numbers', 'Введите оба числа: сколько должно быть и сколько получилось.');
            if (window.__toast) window.__toast(msg, 'warning');
            return;
        }
        st.correction = S.correction({ scale: r.scale, dx: st.correction.dx, dy: st.correction.dy });
        saveCorrection(); renderCorrection(); schedule();
        if (window.__toast) {
            window.__toast(fill(tr('calib_applied', 'Поправка :s % сохранена'), { s: S.fmt(r.offBy, 2) }), 'success');
        }
        if (window.__aeFunnel) {
            window.__aeFunnel('calibrate_scale', {
                offBy: Math.round(r.offBy * 100) / 100,
                dx: st.correction.dx, dy: st.correction.dy,
                format: st.fmtId,
            });
        }
    }

    /* ── export ──────────────────────────────────────────────────────────── */
    function exporting(btn, on_) {
        if (!btn) return;
        btn.disabled = on_;
        btn.classList.toggle('is-busy', on_);
    }

    function exportPdf() {
        var f = currentFormat();
        var list = cellsForSheet();
        var laid = layoutCells(list);
        exporting(els.btnPdf, true);
        if (window.__stage) window.__stage('export');

        // Count the pre-flight findings that were on screen at the moment of
        // export. A user who exports ANYWAY with 12 records overflowing is
        // telling us the warning is not working — that is only visible if the
        // warning state travels with the export, not separately from it.
        var warned = { overflow: 0, tiny: 0 };
        laid.forEach(function (c) {
            if (!c) return;
            c.issues.forEach(function (i) {
                if (i.code === 'text_overflow') warned.overflow++;
                else if (i.code === 'text_tiny') warned.tiny++;
            });
        });
        var t0 = Date.now();

        return PDF.build({
            fmt: f,
            correction: st.correction,
            cells: laid,
            order: st.order,
            outline: els.optOutline.checked,
            cutMarks: els.optCut.checked,
            ruler: els.optRuler.checked,
            rulerCaption: tr('ruler_caption', '50 мм — проверьте линейкой'),
        }).then(function (res) {
            var name = fill(tr('file_name', 'nakleyki-:n-na-liste.pdf'), { n: S.perSheet(f) });
            window.__downloadFile(res.blob, name);
            if (window.__aeToolComplete) {
                window.__aeToolComplete({
                    format: f.id,
                    src: f.src,                       // exact / spec / derived / custom
                    page: f.page,                     // a4 / letter
                    shape: f.shape,
                    cells: res.cells, pages: res.pages,
                    mode: st.dataMode, preset: st.presetId,
                    // The three features whose whole point is that they exist:
                    // if none of them is ever used, they are in the wrong place.
                    corrected: (Math.abs(st.correction.scale - 1) > 1e-6 || !!st.correction.dx || !!st.correction.dy) ? 1 : 0,
                    skip: st.skip, copies: st.copies,
                    ruler: els.optRuler.checked ? 1 : 0,
                    cut: els.optCut.checked ? 1 : 0,
                    outline: els.optOutline.checked ? 1 : 0,
                    // Exported despite the tool saying it would not fit.
                    warn_overflow: warned.overflow,
                    warn_tiny: warned.tiny,
                    barcodes: st.elements.filter(function (e) { return e.type === 'barcode' && !e.hidden; })
                        .map(function (e) { return e.symbology; }).join(',') || null,
                    build_ms: Date.now() - t0,
                });
            }
        }).catch(function (e) {
            // A failed export is the one thing that must never be silent: the
            // user is left with no file and no explanation, and __handleError
            // alone only reaches the console.
            if (window.__aeToolError) {
                window.__aeToolError({ stage: 'pdf_build', format: f.id, cells: list.length,
                    msg: String(e && e.message || e).slice(0, 180) });
            }
            window.__handleError(e, SLUG + ':pdf');
        }).then(function () { exporting(els.btnPdf, false); });
    }

    function exportOverlay() {
        var f = currentFormat();
        exporting(els.btnOverlay, true);
        return PDF.buildOverlay(f, {
            correction: st.correction,
            title: tr('overlay_title', 'Проверочный лист — печатайте на обычной бумаге, 100 %, без «по размеру страницы»'),
        }).then(function (res) {
            window.__downloadFile(res.blob, 'proverochnyy-list.pdf');
            if (window.__aeFunnel) window.__aeFunnel('overlay', { format: f.id });
        }).catch(function (e) {
            if (window.__aeToolError) {
                window.__aeToolError({ stage: 'overlay_build', format: f.id,
                    msg: String(e && e.message || e).slice(0, 180) });
            }
            window.__handleError(e, SLUG + ':overlay');
        }).then(function () { exporting(els.btnOverlay, false); });
    }

    /* ── persistence ─────────────────────────────────────────────────────── */
    function persist() {
        if (!window.__toolSettings) return;
        try {
            window.__toolSettings.save(SLUG, 'state', {
                fmtId: st.fmtId, custom: st.custom, presetId: st.presetId,
                elements: st.elements, dataMode: st.dataMode, listText: st.listText,
                seq: st.seq, skip: st.skip, copies: st.copies, order: st.order,
            });
        } catch (e) { /* quota or private mode — not worth surfacing */ }
    }

    function restore() {
        if (!window.__toolSettings) return false;
        var s = null;
        try { s = window.__toolSettings.load(SLUG, 'state', null); } catch (e) { return false; }
        if (!s || typeof s !== 'object') return false;
        if (s.fmtId) st.fmtId = s.fmtId;
        if (s.custom) st.custom = S.custom(s.custom);
        if (s.presetId) st.presetId = s.presetId;
        if (Array.isArray(s.elements) && s.elements.length) st.elements = s.elements;
        if (s.dataMode) st.dataMode = s.dataMode;
        if (typeof s.listText === 'string') st.listText = s.listText;
        if (s.seq) st.seq = s.seq;
        st.skip = +s.skip || 0;
        st.copies = Math.max(1, +s.copies || 1);
        st.order = s.order === 'col' ? 'col' : 'row';
        return true;
    }

    /* ── boot ────────────────────────────────────────────────────────────── */
    function cache() {
        ['fmt', 'geom', 'geomWarn', 'presets', 'elList', 'editor', 'editorWrap', 'sheet', 'one',
            'summary', 'runWarn', 'pageLabel', 'pagePrev', 'pageNext', 'dataTabs',
            'd_list', 'd_table', 'd_seq', 'listInput', 'tableInput', 'tableFile', 'tablePreview',
            'tableNote', 'skip', 'copies', 'order', 'optOutline', 'optCut', 'optRuler',
            'showNumbers', 'showSafe', 'showDead', 'btnPdf', 'btnOverlay', 'calibBadge',
            'k_scale', 'k_dx', 'k_dy', 'k_nominal', 'k_measured', 'k_apply', 'k_reset',
            'customDlg', 'c_cols', 'c_rows', 'c_w', 'c_h', 'c_x0', 'c_y0', 'c_dx', 'c_dy',
            'c_shape', 'c_radius', 'c_auto', 'c_summary', 'c_preview', 'c_ok', 'c_cancel',
            'addText', 'addBarcode', 'addImage', 'addShape', 'undo',
            'btnSave', 'designFile',
        ].forEach(function (id) { els[id] = $('lm-' + id); });
    }

    /* ── шаги ────────────────────────────────────────────────────────────────
     * Открыт один шаг. Это не косметика: с четырьмя развёрнутыми панель была
     * 2483 px, и лист уезжал на два экрана вверх ровно тогда, когда его надо
     * видеть. Закрытый шаг не прячет настройки — он показывает их результат
     * («24 на листе, 70×37 мм») и открывается щелчком.
     *
     * ⚠️ Ничего не прокручиваем: страница не должна ездить под человеком. */
    function openStep(n) {
        var cards = document.querySelectorAll('.lm-card[data-step]');
        for (var i = 0; i < cards.length; i++) {
            var open = cards[i].getAttribute('data-step') === String(n);
            cards[i].setAttribute('data-open', open ? '1' : '0');
            var head = cards[i].querySelector('.lm-head');
            if (head) head.setAttribute('aria-expanded', open ? 'true' : 'false');
        }
        if (window.__aeFunnel) window.__aeFunnel('step', { n: Number(n) });
    }

    function bindSteps() {
        var heads = document.querySelectorAll('.lm-card[data-step] .lm-head');
        for (var i = 0; i < heads.length; i++) {
            (function (head) {
                on(head, 'click', function () {
                    var card = head.parentNode;
                    var n = card.getAttribute('data-step');
                    openStep(card.getAttribute('data-open') === '1' ? 0 : n);
                });
            })(heads[i]);
        }
    }

    /* Строка под номером шага, когда он закрыт. Пишем ответ, а не название
     * настройки: «24 на листе» полезнее, чем «формат листа выбран». */
    function renderStepSummaries(list, plan) {
        var f = currentFormat();
        var sums = {
            1: fill(tr('step_sum_sheet', ':n · :w×:h мм'), {
                n: S.perSheet(f), w: fmtMm(f.w), h: fmtMm(f.h),
            }),
            2: fill(tr('step_sum_design', ':n'), { n: st.elements.length }),
            3: tr('dm_' + st.dataMode, st.dataMode),
            4: plan ? fill(tr('step_sum_print', ':sheets'), plan) : '',
        };
        var nodes = document.querySelectorAll('.lm-sum[data-sum]');
        for (var i = 0; i < nodes.length; i++) {
            var k = nodes[i].getAttribute('data-sum');
            nodes[i].textContent = sums[k] == null ? '' : sums[k];
        }
    }

    function fmtMm(v) {
        var r = Math.round(Number(v) * 10) / 10;
        return String(r).replace('.', ',');
    }

    function bind() {
        bindSteps();

        on(els.fmt, 'change', function () {
            st.fmtId = els.fmt.value;
            if (st.fmtId === 'custom') { openCustom(); return; }
            st.page = 0;
            renderPresetGallery();
            schedule();
            if (window.__aeFunnel) window.__aeFunnel('format', { id: st.fmtId });
        });

        on(els.c_ok, 'click', function () {
            st.custom = readCustom(els.c_auto.checked);
            // The registry cannot know every pack sold. Every custom grid a user
            // types is a format we do not have — and if the same numbers keep
            // arriving, that pack belongs in the registry. Rounded to 0.1 mm so
            // near-identical entries group instead of scattering.
            if (window.__aeFunnel) {
                window.__aeFunnel('custom_sheet', {
                    cols: st.custom.cols, rows: st.custom.rows,
                    w: Math.round(st.custom.w * 10) / 10,
                    h: Math.round(st.custom.h * 10) / 10,
                    shape: st.custom.shape,
                    auto: els.c_auto.checked ? 1 : 0,
                    per: S.perSheet(st.custom),
                });
            }
            st.fmtId = 'custom';
            els.customDlg.hidden = true;
            buildFormatSelect();
            els.fmt.value = 'custom';
            renderPresetGallery();
            schedule();
        });
        on(els.c_cancel, 'click', function () {
            els.customDlg.hidden = true;
            els.fmt.value = st.fmtId;
        });
        ['c_cols', 'c_rows', 'c_w', 'c_h', 'c_x0', 'c_y0', 'c_dx', 'c_dy', 'c_shape', 'c_radius', 'c_auto']
            .forEach(function (k) { on(els[k], 'input', syncCustomPreview); on(els[k], 'change', syncCustomPreview); });

        Array.prototype.forEach.call(els.dataTabs.querySelectorAll('button'), function (b) {
            on(b, 'click', function () {
                st.dataMode = b.dataset.mode;
                st.page = 0;
                renderDataPanel();
                schedule();
                if (window.__stage) window.__stage('data_' + st.dataMode);
            });
        });

        on(els.listInput, 'input', function () { st.listText = els.listInput.value; schedule(); });
        on(els.tableInput, 'input', function () { ingestTable(els.tableInput.value); renderEditor(); schedule(); });

        on(els.tableFile, 'change', function () {
            var file = els.tableFile.files && els.tableFile.files[0];
            if (!file) return;
            // Encoding sniffing, not FileReader.readAsText: a Russian Excel CSV
            // is windows-1251 and would arrive as mojibake otherwise.
            file.arrayBuffer().then(function (buf) {
                var dec = window.SubtitleParser
                    ? window.SubtitleParser.decodeBytes(buf)
                    : { text: new TextDecoder().decode(buf), encoding: 'utf-8' };
                els.tableInput.value = dec.text;
                ingestTable(dec.text);
                st.dataMode = 'table';
                renderDataPanel();
                schedule();
                if (window.__aeFunnel) window.__aeFunnel('csv_import', { encoding: dec.encoding });
            }).catch(function (e) { window.__handleError(e, SLUG + ':csv'); });
        });

        ['prefix', 'start', 'step', 'count', 'pad', 'suffix'].forEach(function (k) {
            var node = $('lm-seq_' + k);
            if (!node) return;
            on(node, 'input', function () {
                st.seq[k] = (k === 'prefix' || k === 'suffix') ? node.value : parseInt(node.value, 10);
                schedule();
            });
        });

        on(els.skip, 'input', function () {
            // "Skip" means cells already peeled off THIS sheet, so it cannot
            // exceed one sheet: a bigger number just means you should feed a
            // different sheet first. Left unclamped it silently pushed every
            // label past page 1 and the user stared at a blank preview with
            // nothing saying the content was on page 5.
            var per = S.perSheet(currentFormat());
            var raw = Math.max(0, parseInt(els.skip.value, 10) || 0);
            var v = Math.min(raw, per - 1);
            if (v !== raw) {
                els.skip.value = v;
                if (window.__toast) {
                    window.__toast(fill(tr('skip_clamped', ''), { per: per }), 'info');
                }
            }
            st.skip = v;
            st.page = 0;
            schedule();
        });
        on(els.copies, 'input', function () { st.copies = Math.max(1, parseInt(els.copies.value, 10) || 1); schedule(); });
        on(els.order, 'change', function () { st.order = els.order.value; schedule(); });
        [els.optRuler, els.showNumbers, els.showSafe, els.showDead].forEach(function (n) {
            on(n, 'change', function () { st.ruler = els.optRuler.checked; schedule(); });
        });
        on(els.optOutline, 'change', schedule);
        on(els.optCut, 'change', schedule);

        on(els.pagePrev, 'click', function () { st.page = Math.max(0, st.page - 1); schedule(); });
        on(els.pageNext, 'click', function () { st.page = st.page + 1; schedule(); });

        on(els.addText, 'click', function () {
            pushUndo();
            st.elements.push({ type: 'text', text: tr('new_text', 'Текст'), x: 0.1, y: 0.35, w: 0.8, h: 0.3, sizeRel: 0.2, align: 'center', valign: 'middle', font: 'noto' });
            st.selected = st.elements.length - 1;
            renderElementList(); renderEditor(); schedule();
        });
        on(els.addBarcode, 'click', function () {
            pushUndo();
            st.elements.push({ type: 'barcode', symbology: 'qrcode', text: '{{value}}', x: 0.3, y: 0.1, w: 0.4, h: 0.8 });
            st.selected = st.elements.length - 1;
            renderElementList(); renderEditor(); schedule();
        });
        on(els.addImage, 'click', function () {
            pushUndo();
            st.elements.push({ type: 'image', x: 0.05, y: 0.05, w: 0.35, h: 0.9, fit: 'contain' });
            st.selected = st.elements.length - 1;
            renderElementList(); renderEditor(); schedule();
        });
        on(els.addShape, 'click', function () {
            pushUndo();
            st.elements.push({ type: 'shape', shape: 'rect', x: 0.02, y: 0.02, w: 0.96, h: 0.96, stroke: '#333333', strokeWidth: 0.3 });
            st.selected = st.elements.length - 1;
            renderElementList(); renderEditor(); schedule();
        });

        on(els.undo, 'click', function () {
            if (!undo() && window.__toast) window.__toast(tr('undo_empty', ''), 'info');
        });
        // Ctrl/Cmd+Z, but never while the user is typing into a field — there
        // the browser's own text undo is what they mean.
        document.addEventListener('keydown', function (e) {
            if ((window.__keyLetter ? window.__keyLetter(e) : String(e.key || '').toLowerCase()) !== 'z' || !(e.ctrlKey || e.metaKey) || e.shiftKey) return;
            var t = e.target;
            if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
            if (!document.getElementById('lm-app')) return;
            if (undo()) e.preventDefault();
        });

        on(els.k_apply, 'click', applyMeasuredScale);
        on(els.k_reset, 'click', function () {
            st.correction = { scale: 1, dx: 0, dy: 0 };
            saveCorrection(); renderCorrection(); schedule();
        });
        [['k_dx', 'dx'], ['k_dy', 'dy']].forEach(function (p) {
            on(els[p[0]], 'input', function () {
                var v = parseFloat(String(els[p[0]].value).replace(',', '.'));
                st.correction[p[1]] = isFinite(v) ? v : 0;
                saveCorrection(); renderCorrection(); schedule();
            });
        });
        on(els.k_scale, 'input', function () {
            var v = parseFloat(String(els.k_scale.value).replace(',', '.'));
            st.correction = S.correction({ scale: 1 + (isFinite(v) ? v : 0) / 100, dx: st.correction.dx, dy: st.correction.dy });
            saveCorrection(); schedule();
        });

        attachLabelDrag(els.one);
        /* Save / open a design.
         *
         * The browser already remembers the last design, but only on THIS
         * device and only until someone clears site data. A jar label made once
         * a year, or a class list made on a school computer and reprinted at
         * home, needs a file. It is deliberately just the design — the sheet
         * format travels with it, the DATA does not: a list of names is not
         * something to hand around by accident. */
        on(els.btnSave, 'click', function () {
            var doc = {
                app: 'timbrica-label-maker', v: 1,
                format: st.fmtId, custom: st.fmtId === 'custom' ? st.custom : null,
                preset: st.presetId, elements: st.elements,
            };
            var blob = new Blob([JSON.stringify(doc, null, 1)], { type: 'application/json' });
            window.__downloadFile(blob, tr('design_file', 'nakleyka.json'));
            if (window.__aeFunnel) window.__aeFunnel('design_save', { n: st.elements.length });
        });

        on(els.designFile, 'change', function () {
            var file = els.designFile.files && els.designFile.files[0];
            if (!file) return;
            file.text().then(function (txt) {
                var doc;
                try { doc = JSON.parse(txt); } catch (e) { doc = null; }
                // Reject anything that is not ours, and say so — a silently
                // ignored file is the worst possible answer to "open".
                if (!doc || doc.app !== 'timbrica-label-maker' || !Array.isArray(doc.elements)) {
                    if (window.__toast) window.__toast(tr('design_bad', ''), 'error');
                    els.designFile.value = '';
                    return;
                }
                pushUndo();
                st.elements = doc.elements;
                st.presetId = doc.preset || st.presetId;
                if (doc.custom) { st.custom = S.custom(doc.custom); st.fmtId = 'custom'; }
                else if (doc.format && S.byId(doc.format)) st.fmtId = doc.format;
                buildFormatSelect();
                els.fmt.value = st.fmtId;
                st.selected = 0;
                renderPresetGallery(); renderElementList(); renderEditor(); schedule();
                if (window.__toast) window.__toast(tr('design_loaded', ''), 'success');
                if (window.__aeFunnel) window.__aeFunnel('design_open', { n: st.elements.length });
                els.designFile.value = '';
            }).catch(function () {
                if (window.__toast) window.__toast(tr('design_bad', ''), 'error');
            });
        });

        on(els.btnPdf, 'click', exportPdf);
        on(els.btnOverlay, 'click', exportOverlay);
    }

    function start() {
        if (!S || !D || !LO || !R || !P || !PDF) {
            if (window.__toast) window.__toast(tr('err_boot', 'Не удалось загрузить инструмент. Обновите страницу.'), 'error');
            return;
        }
        cache();
        buildFormatSelect();
        loadCorrection();
        var restored = restore();
        if (!restored || !st.elements.length) applyPreset(st.presetId);
        els.fmt.value = S.byId(st.fmtId) || st.fmtId === 'custom' ? st.fmtId : 'div-24';
        if (st.listText) els.listInput.value = st.listText;
        els.skip.value = st.skip;
        els.copies.value = st.copies;
        els.order.value = st.order;
        bind();
        renderCorrection();
        renderDataPanel();
        renderPresetGallery();
        renderElementList();
        renderEditor();

        measure = R.measurer(familyFor);
        draw();
        if (window.__stage) window.__stage('ready');

        // Metrics from a fallback face produce line breaks that change under
        // the user's hands once the real font arrives, so re-layout when the
        // faces are actually available.
        R.fontsReady(FONTS.map(function (f) { return '"' + f[1] + '"'; })).then(function () {
            measure = R.measurer(familyFor);
            renderPresetGallery();
            schedule();
        });
    }

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
    else start();
})();
